/**
 * Projects a Mission Control `SessionEvent[]` transcript into {@link Artifact}s.
 *
 * Used by cloud and CLI agent sessions, which render from Mission Control's
 * persisted event stream rather than an AHP `ChatState`. See `artifact-core`
 * for the shared derivation and its coverage gaps.
 *
 * Both fields read here come from events Mission Control persists verbatim and
 * returns in full from `GET /agents/tasks/{taskId}/events`, so the list is
 * identical on a reload as it was live:
 *   - `tool.execution_start` carries `toolName` and the complete `arguments`.
 *   - `tool.execution_complete` carries `success` and the result text.
 * The two are paired by `toolCallId`. A start with no matching complete is an
 * in-flight call and is skipped, exactly as an unsettled AHP tool call is.
 */

import type {SessionEvent, ToolExecutionCompleteEvent, ToolExecutionStartEvent} from '../../types/events'
import {collectArtifacts, isRecord, type Artifact, type ToolWriteRecord} from './artifact-core'

const TOOL_EXECUTION_START = 'tool.execution_start'
const TOOL_EXECUTION_COMPLETE = 'tool.execution_complete'

function isStart(event: SessionEvent): event is ToolExecutionStartEvent {
  return event.type === TOOL_EXECUTION_START
}

function isComplete(event: SessionEvent): event is ToolExecutionCompleteEvent {
  return event.type === TOOL_EXECUTION_COMPLETE
}

/**
 * Prefer `detailedContent`, which Mission Control documents as the "full
 * detailed tool result for UI display, preserving complete content such as
 * diffs", falling back to the token-efficient `content` when it is absent.
 */
function resultText(event: ToolExecutionCompleteEvent): string | undefined {
  const {result} = event.data
  const text = result?.detailedContent ?? result?.content
  return text?.trim() || undefined
}

function* toolWriteRecords(events: readonly SessionEvent[]): Generator<ToolWriteRecord> {
  const completions = new Map<string, ToolExecutionCompleteEvent>()
  for (const event of events) {
    if (isComplete(event)) completions.set(event.data.toolCallId, event)
  }

  for (const event of events) {
    if (!isStart(event)) continue

    const {toolCallId, toolName} = event.data
    const completion = completions.get(toolCallId)
    // No completion means the call never settled, so nothing was written yet.
    if (!completion) continue

    // `arguments` is generated as `Record<string, never>` from a free-form
    // OpenAPI object, which makes every lookup `never`; widen it to read keys.
    const args: Record<string, unknown> = isRecord(event.data.arguments) ? event.data.arguments : {}
    const preview = resultText(completion)

    yield {
      toolCallId,
      toolName,
      // Arguments already arrive as an object here, so a bare-scalar input —
      // the case `primaryKey` exists for in the AHP projection — cannot occur.
      resolveArgs: () => args,
      succeeded: completion.data.success === true,
      ...(preview === undefined ? {} : {preview}),
    }
  }
}

/** Reduce a Mission Control event stream to the files the agent wrote. */
export function deriveArtifactsFromSessionEvents(events: readonly SessionEvent[] | undefined): Artifact[] {
  if (!events || events.length === 0) return []
  return collectArtifacts(toolWriteRecords(events))
}
